import { useState, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  submitRating,
  deleteRating,
  RatingWithReview,
} from '../api/marketplaceApi';
import { MarketplaceTargetType } from 'types/marketplace';

export interface UseSubmitRatingResult {
  submit: (params: {
    targetType: MarketplaceTargetType;
    targetId: string;
    stars: number;
    review?: { title?: string | null; body: string };
  }) => Promise<RatingWithReview | null>;
  remove: (ratingId: string) => Promise<boolean>;
  submitting: boolean;
  deleting: boolean;
  error: string | null;
}

export function useSubmitRating(
  onOptimisticSubmit?: (rating: RatingWithReview) => void,
  onOptimisticDelete?: (ratingId: string) => void,
  onRevert?: () => void
): UseSubmitRatingResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (params: {
      targetType: MarketplaceTargetType;
      targetId: string;
      stars: number;
      review?: { title?: string | null; body: string };
    }): Promise<RatingWithReview | null> => {
      if (!apiClients?.schemaClient || !organizationId) {
        return null;
      }

      setSubmitting(true);
      setError(null);

      // Build optimistic rating for immediate UI feedback
      const optimisticRating: RatingWithReview = {
        id: 'optimistic-' + Date.now(),
        target_type: params.targetType,
        target_id: params.targetId,
        user_id: '',
        tenant_id: organizationId,
        stars: params.stars,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        review: params.review
          ? {
              id: 'optimistic-review-' + Date.now(),
              rating_id: 'optimistic-' + Date.now(),
              title: params.review.title ?? null,
              body: params.review.body,
              is_edited: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }
          : undefined,
      };

      if (onOptimisticSubmit) {
        onOptimisticSubmit(optimisticRating);
      }

      try {
        const response = await submitRating(apiClients.schemaClient, {
          targetType: params.targetType,
          targetId: params.targetId,
          stars: params.stars,
          review: params.review,
        });
        return response.rating;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to submit rating';
        setError(message);
        if (onRevert) {
          onRevert();
        }
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [apiClients, organizationId, onOptimisticSubmit, onRevert]
  );

  const remove = useCallback(
    async (ratingId: string): Promise<boolean> => {
      if (!apiClients?.schemaClient) {
        return false;
      }

      setDeleting(true);
      setError(null);

      if (onOptimisticDelete) {
        onOptimisticDelete(ratingId);
      }

      try {
        await deleteRating(apiClients.schemaClient, ratingId);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete rating';
        setError(message);
        if (onRevert) {
          onRevert();
        }
        return false;
      } finally {
        setDeleting(false);
      }
    },
    [apiClients, onOptimisticDelete, onRevert]
  );

  return { submit, remove, submitting, deleting, error };
}
